import { Text } from "@imminently/react/ui/core/text";
import { Badge, Box, Button, MenuList, Popover, Stack, CircularProgress } from "@mui/material";
import * as _ from "lodash";
import { useState } from "react";
import { useQuery } from "react-query";

import { usePopover } from "@/components/ContextMenu";
import { Icon } from "@/components/Icon";
import FilterOption from "@/components/FilterOption";
import { PatientTimeLine } from "@/components/Patient/PatientTimeline";
import {
  ActivityFilterSections,
  ActivityFilters,
  PatientEncounter,
} from "@/resources/types";
import { css } from "@/theme";
import global from "@/global";

const styles = {
  popover: {
    pt: 2,
    pb: 0.5,
    "& > .MuiTypography-root:not(:first-child)": {
      mt: 2,
    },
  },
  filtersCountWrapper: {
    width: "20px",
    pl: "13px",
    pb: "3px",
  },
  filtersCount: {
    "& .MuiBadge-badge": {
      color: "error.dark",
      backgroundColor: "error.light",
    },
  },
  clearFiltersBtn: css`
    height: fit-content;
    padding: 0 8px 0 0;
    float: right;
    margin: 14px 12px 14px 6px;

    & .MuiButton-startIcon {
      margin: 0;
    }
  `,
  resetFiltersBtn: css`
    height: fit-content;
    padding: 0 0px 0 0;
    float: left;
    margin: 14px 6px 14px 12px;

    & .MuiButton-startIcon {
      margin: 0;
    }
  `,
  // Original styling when there was no reset filters button, use this again if we need to remove reset filters button
  // clearFiltersBtn: css`
  //   height: fit-content;
  //   padding: 0 8px 0 0;
  //   float: right;
  //   margin: 14px 12px 14px 40px;

  //   & .MuiButton-startIcon {
  //     margin: 0;
  //   }
  // `,
};

const FILTERS = {
  [ActivityFilterSections.INPUT]: {
    label: "Input",
    filters: {
      snrDoctor: "Senior Doctor",
      doctor: "Doctor",
      nurse: "Nurse",
      other: "Other",
      System: "System",
    },
  },
  [ActivityFilterSections.CATEGORY]: {
    label: "Category",
    filters: {
      allergy: "Allergy",
      task: "Task",
      location: "Location",
      triage: "Triage",
      observation: "Observation",
      communication: "Communication",
      note: "Note",
      comment: "Comment",
      create: "Create",
    },
  },
  [ActivityFilterSections.SYSTEM]: {
    label: "System",
    filters: {
      encounterEvents: "Encounter Events",
      outboundMessages: "Outbound Messages",
      inboundMessages: "Inbound Messages",
    },
  },
};

// Default filters - all category filters are selected by default
const initialFilters: ActivityFilters = {
  [ActivityFilterSections.CATEGORY]: [
    "allergy",
    "task",
    "location",
    "triage",
    "observation",
    "communication",
    "note",
    "comment",
    "create",
  ],
};

type FilterSectionProps = {
  properties: { label: string; filters: { [key: string]: string } };
  state?: string[];
  updateFilter: (filter: string) => void;
  selectAll: () => void;
};

const FilterSection = (props: FilterSectionProps) => {
  const { properties, state, updateFilter, selectAll } = props;
  const { label, filters } = properties;
  const allFilterKeys = Object.keys(filters);
  const allSelected = state && allFilterKeys.length > 0 && allFilterKeys.every(key => state.includes(key));
  const someSelected = state && allFilterKeys.some(key => state.includes(key));

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mx: 2, mb: 1 }}>
        <Text sx={{ fontSize: "0.75rem" }} variant="h6">
          {label}
        </Text>
        <Button
          variant="text"
          size="small"
          onClick={selectAll}
          sx={{ 
            minWidth: "auto",
            padding: "4px 8px",
            fontSize: "0.7rem",
            textTransform: "none"
          }}
        >
          {allSelected ? "Deselect All" : "Select All"}
        </Button>
      </Stack>

      <Stack gap={0.5}>
        {Object.keys(filters).map((filter, index) => (
          <FilterOption
            key={index}
            name={filters[filter]}
            checked={state ? state.includes(filter) : false}
            onClick={() => updateFilter(filter)}
          />
        ))}
      </Stack>
    </>
  );
};

type PatientActivitiesSectionProps = { encounter: PatientEncounter };

const PatientActivitiesSection = (props: PatientActivitiesSectionProps) => {
  const { encounter } = props;
  const {
    onClick: openFilters,
    onClose: closeFilters,
    ...popoverProps
  } = usePopover("patient-activities-filters", {
    anchorOrigin: { vertical: "bottom", horizontal: "right" },
    transformOrigin: { vertical: "top", horizontal: "right" },
  });

  const [filters, setFilters] = useState<ActivityFilters>(initialFilters);

  // Fetch events data using direct client call with React Query
  const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useQuery(
    ["encounters", encounter.id, "events"],
    async () => {
      const response = await global.client.get(`encounters/${encounter.id}/events`);
      return response.data;
    }
  );

  const clearFilters = () => setFilters({});
  const resetFilters = () => setFilters(initialFilters);
  const updateFilters = (section: ActivityFilterSections, filter: string) => {
    setFilters((oldFilters) => {
      if (oldFilters === null) {
        return { [section]: [filter] };
      }

      if (oldFilters[section] === undefined) {
        return { ...oldFilters, [section]: [filter] };
      }

      return _.omitBy(
        {
          ...oldFilters,
          [section]: _.xor(oldFilters[section], [filter]),
        },
        _.isEmpty
      );
    });
  };

  const selectAllInSection = (section: ActivityFilterSections) => {
    setFilters((oldFilters) => {
      const sectionFilters = FILTERS[section];
      const allKeys = Object.keys(sectionFilters.filters) as string[];
      const currentSelection = (oldFilters[section] || []) as string[];
      const allSelected = allKeys.length > 0 && allKeys.every(key => currentSelection.includes(key));

      if (allSelected) {
        // Deselect all - remove the section if it becomes empty
        return _.omitBy(
          {
            ...oldFilters,
            [section]: [],
          },
          (value) => Array.isArray(value) && value.length === 0
        );
      } else {
        // Select all
        return {
          ...oldFilters,
          [section]: allKeys,
        };
      }
    });
  };

  const filterSections = Object.keys(FILTERS).map((section, index) => {
    const sectionName = section as ActivityFilterSections;

    return (
      <FilterSection
        key={index}
        properties={FILTERS[sectionName]}
        state={filters[sectionName]}
        updateFilter={(filter) => updateFilters(sectionName, filter)}
        selectAll={() => selectAllInSection(sectionName)}
      />
    );
  });

  const filtersCount = _.sum(
    Object.values(filters).map((section) => section.length)
  );

  return (
    <>
      <Stack alignItems="center" justifyContent="space-between" direction="row">
        <Text variant="h4">Activities</Text>
        <Stack direction="row" alignItems="center" gap={1}>
          {eventsLoading && (
            <CircularProgress size={20} />
          )}
          <Button
            variant="outlined"
            endIcon={<Icon icon="chevrondown" />}
            onClick={openFilters}
          >
            Filters
            <Box sx={filtersCount > 0 ? styles.filtersCountWrapper : undefined}>
              <Badge sx={styles.filtersCount} badgeContent={filtersCount} />
            </Box>
          </Button>
        </Stack>
        <Popover onClose={closeFilters} {...popoverProps} disableScrollLock>
          <MenuList sx={styles.popover}>
            {filterSections}
            <Button
              css={styles.resetFiltersBtn}
              variant="text"
              size="small"
              startIcon={<Icon height={16} icon="retry" />}
              disabled={_.isEqual(filters, initialFilters)}
              onClick={resetFilters}
            >
              Reset filters
            </Button>
            <Button
              css={styles.clearFiltersBtn}
              variant="text"
              size="small"
              startIcon={<Icon icon="clear" />}
              disabled={_.isEmpty(filters)}
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          </MenuList>
        </Popover>
      </Stack>
      <PatientTimeLine encounter={encounter} filters={filters} events={eventsData?.events || []} />
    </>
  );
};

export default PatientActivitiesSection;
